import { can } from '@misumi/shared'
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  EditOutlined,
  FolderOutlined,
  PlusOutlined,
  UsergroupAddOutlined,
} from '../../components/icons'
import { Avatar, AvatarStack } from '../../components/ui/Avatar'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyState } from '../../components/ui/EmptyState'
import { Icon } from '../../components/ui/Icon'
import { Skeleton } from '../../components/ui/Skeleton'
import { Tabs } from '../../components/ui/Tabs'
import { useToast } from '../../components/ui/ToastProvider'
import { useRole, useSession } from '../../lib/auth/use-session'
import { displayNameFor } from '../../lib/fixtures/sourceable-lookup'
import { useLoadMoreSentinel } from '../../lib/list/useLoadMoreSentinel'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import { OrderTable } from '../orders/OrderTable'
import type { OrderTableSortColumn, SortDirection } from '../orders/OrderTable'
import { resolveOrderIdentity } from '../orders/domain'
import { CreateRevisionModal } from '../parts/components/CreateRevisionModal'
import type { LibraryPartRow, PartDetail } from '../parts/types'
import { defaultPartsLibraryService, usePartsLibrary } from '../parts/usePartsLibrary'
import type { PartsLibraryService } from '../parts/usePartsLibrary'
import { NewRfqModal } from '../rfq/NewRfqModal'
import { rfqNumberDisplay } from '../rfq/domain'
import type { RfqLineView } from '../rfq/domain'
import { AddMemberModal } from './components/AddMemberModal'
import { NotAMemberOverlay } from './components/NotAMemberOverlay'
import { WorkspaceBomsTab } from './components/WorkspaceBomsTab'
import { WorkspacePartsTab } from './components/WorkspacePartsTab'
import { WorkspaceRfqsTab } from './components/WorkspaceRfqsTab'
import { WorkspaceUploadMenu } from './components/WorkspaceUploadMenu'
import { formatDate } from './format'
import { isRedactedWorkspace } from './types'
import type { WorkspaceOrRedacted } from './types'
import { defaultWorkspaceOrdersService, useWorkspaceOrders } from './useWorkspaceOrders'
import type { WorkspaceOrdersService } from './useWorkspaceOrders'
import { defaultWorkspaceRfqsService, useWorkspaceRfqs } from './useWorkspaceRfqs'
import type { WorkspaceRfqsService } from './useWorkspaceRfqs'
import { defaultWorkspacesService, useWorkspace } from './useWorkspaces'
import type { WorkspacesService } from './useWorkspaces'

export interface WorkspacePageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: WorkspacesService
  /** UID-299 (Slice C) — the SAME `PartsLibraryService` seam
   * (`usePartsLibrary.ts`) the Parts Library screen uses, called here with
   * `?workspaceId` so the Parts/BOMs tabs render this workspace's slice of
   * `GET /parts` rather than a placeholder. Injectable for tests. */
  readonly partsLibraryService?: PartsLibraryService
  /** UID-300 — the workspace-scoped `GET /rfq?workspaceId=` seam backing the
   * RFQs tab + the header stat card's `partCount`/`orderCount` siblings.
   * Injectable for tests. */
  readonly rfqsService?: WorkspaceRfqsService
  /** UID-300 — the workspace-scoped `GET /orders?workspaceId=` seam backing
   * the Orders tab + the header stat card's `orderCount`. Injectable for
   * tests. */
  readonly ordersService?: WorkspaceOrdersService
}

type WorkspaceTab = 'parts' | 'boms' | 'rfqs' | 'orders' | 'members'
const SKELETON_KEYS = ['a', 'b', 'c']

const TAB_SEARCH_PLACEHOLDER: Record<WorkspaceTab, string> = {
  parts: 'Search parts by name or SKU',
  // UID-413 finding 2 — comp `phByTab.boms` (`Fictiv Platform.dc.html:9954`)
  // is "Filter by BOM name", matching the "Filter…" wording already used
  // elsewhere in this same object for the tabs that follow the comp exactly.
  boms: 'Filter by BOM name',
  rfqs: 'Search RFQs by part name or number',
  orders: 'Search orders by number or name',
  members: 'Filter by member name',
}

/** Case-insensitive substring match; `undefined`/`null` values never match. */
function matchesSearch(needle: string, ...haystacks: readonly (string | null | undefined)[]) {
  if (!needle) return true
  const lowerNeedle = needle.toLowerCase()
  return haystacks.some((value) => value?.toLowerCase().includes(lowerNeedle))
}

/** UID-176: the sole/last owner can't leave until another owner is
 * assigned — the server enforces this lockout regardless (last-active-owner
 * rule), this copy is purely the client-side UX affordance explaining it. */
const SOLE_OWNER_LEAVE_EXPLANATION =
  "You're the only owner of this project. Assign another owner before you can leave."

/** UID-178: the delete affordance is shown-but-disabled for non-owners
 * (never hidden) — this is the client-side UX explanation; the server
 * enforces owner-only via `workspace:manage` + `#requireOwner` regardless. */
const DELETE_NON_OWNER_EXPLANATION = 'Only an owner can delete this workspace'

/** UID-364 follow-up: same shown-but-disabled posture as
 * `DELETE_NON_OWNER_EXPLANATION` — the design shows "Add team members"
 * unconditionally, but `WorkspaceService#addMember` is owner-gated
 * server-side (`#requireOwner`), so this is UX-only. */
const ADD_MEMBERS_NON_OWNER_EXPLANATION = 'Only an owner can add team members'

/** UID-364 follow-up: same shown-but-disabled posture — the design shows
 * the rename pencil unconditionally, but `WorkspaceService#updateWorkspace`
 * is owner-gated server-side (`#requireOwner`), so this is UX-only. */
const RENAME_NON_OWNER_EXPLANATION = 'Only an owner can rename this project'

/**
 * Workspace (UID-48b, screens.md §8) — team collaboration container.
 * Renders ONLY the not-a-member overlay when `access === 'redacted'` — the
 * reduced `WorkspaceRedactedStub` (security ruling, UID-48a-app 7e6ff4d)
 * carries no name/stats/tabs data at all, so there's no header/stats/tabs
 * to render in that case, unlike the earlier (fuller) redacted shape this
 * screen was first built against. Full-access workspaces get the complete
 * header/stats/tabs.
 *
 * UID-299/UID-300 closed the earlier cross-vertical gap: Parts/BOMs (UID-299)
 * and RFQs/Orders (UID-300) all now fetch this workspace's own
 * `?workspaceId=` slice (`usePartsLibrary`/`useWorkspaceRfqs`/
 * `useWorkspaceOrders`) rather than a placeholder; only the Members tab was
 * ever backed by data already on the `Workspace` object itself.
 *
 * A thin wrapper so the `key={workspaceId}` below can force a full remount
 * (resetting tab/rename/modal state) when the route param changes — a
 * `key` set on JSX *inside* a component never remounts the component
 * itself, only a key set by whatever renders it does.
 */
export function WorkspacePage(props: WorkspacePageProps = {}) {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>()
  return <WorkspacePageInner key={workspaceId} workspaceId={workspaceId} {...props} />
}

function WorkspacePageInner({
  workspaceId,
  service = defaultWorkspacesService,
  partsLibraryService = defaultPartsLibraryService,
  rfqsService = defaultWorkspaceRfqsService,
  ordersService = defaultWorkspaceOrdersService,
}: WorkspacePageProps & { readonly workspaceId: string }) {
  const { state, refetch, applyMutation } = useWorkspace(workspaceId, service)
  const navigate = useNavigate()
  const { showToast } = useToast()
  // Rules of Hooks: this component has early returns (loading/not_found/
  // error/redacted) below, so every hook — including this one — must be
  // called unconditionally up here, before any of them.
  const session = useSession()
  const role = useRole()
  const canSubmitRfq = role !== null && can(role, 'rfq:submit')
  const canUpload = role !== null && can(role, 'bom:upload')
  const [newRfqModalOpen, setNewRfqModalOpen] = useState(false)

  // UID-299 (Slice C) — ONE `GET /parts?workspaceId=` fetch backs BOTH the
  // Parts and BOMs tabs (the BFF's `LibraryListing` already carries both
  // arrays), so this is a single `usePartsLibrary` call up here rather than
  // two independent fetches. Called unconditionally, alongside `useWorkspace`
  // above, for the same Rules-of-Hooks reason.
  //
  // Code-review C1 (UID-603) — `usePartsLibrary` now paginates the `parts`
  // collection at `PARTS_LIBRARY_PAGE_SIZE` (50); this hook used to be
  // destructured without `loadMore`/`isLoadingMore`/`loadMoreError` at all,
  // so any part past row 50 was unreachable in this tab (and its
  // client-side search at `filteredParts` below could only ever see those
  // 50 rows). Wired the SAME infinite-scroll pattern `PartsLibraryPage.tsx`
  // uses. The backend's `?workspaceId` + `limit`/`cursor` combination is a
  // real, correctly paginated, workspace-scoped read (the concurrent C2
  // fix), so this wiring is not conditional on anything special about this
  // tab.
  const {
    state: libraryState,
    refetch: refetchLibrary,
    addPartsToRfq,
    loadMore: loadMoreLibrary,
    isLoadingMore: isLoadingMoreLibrary,
    loadMoreError: libraryLoadMoreError,
    loadMoreErrorIsCursorRejection: libraryLoadMoreErrorIsCursorRejection,
  } = usePartsLibrary({ workspaceId }, partsLibraryService)
  const hasNextLibraryPage =
    libraryState.status === 'ready' && libraryState.listing.nextCursor != null
  // Code-review C1 (third pass) / H4 (fourth pass) — `recheckKey` forces the
  // observer to re-check the sentinel's current intersection state every
  // time a page is appended, covering the case where the newly-appended
  // page is ALSO filtered away by this tab's client-side `tabSearch`
  // (below), leaving the sentinel still intersecting without ever crossing
  // a fresh threshold, which would otherwise stall pagination one page in.
  // Same pattern as `NewRfqModal.tsx`'s `recheckKey` (see the hook's
  // `recheckKey` doc comment for the full mechanism).
  //
  // H4 (fourth pass) — a bare loaded-item COUNT is lossy: if a page's rows
  // fully dedupe against what's already loaded (`useCursorList.ts`'s
  // `dedupeAppend`), the loaded count doesn't change even though a real
  // fetch completed and `nextCursor` advanced, so the observer never
  // re-checks despite there still being more to load. `nextCursor` itself
  // changes on every successful `loadMore()` independent of whether the
  // page's rows deduped away, so it's used as the recheck key instead — same
  // fix as `RfqListPage.tsx`'s `RfqServerTabPanel` and `NewRfqModal.tsx`.
  const libraryLoadMoreSentinelRef = useLoadMoreSentinel(loadMoreLibrary, hasNextLibraryPage, {
    recheckKey: libraryState.status === 'ready' ? libraryState.listing.nextCursor : null,
  })
  // UID-407 (finding 5) — "Create revision" from the Parts tab's ⋮ menu.
  // Same load-then-open posture as `PartsLibraryPage.handleOpenCreateRevision`:
  // `LibraryPartRow` doesn't carry everything `CreateRevisionModal` needs, so
  // fetch the full `PartDetail` first via the SAME `partsLibraryService`.
  const [revisionPart, setRevisionPart] = useState<PartDetail | null>(null)
  async function handleOpenCreateRevision(item: LibraryPartRow) {
    try {
      setRevisionPart(await partsLibraryService.getPartDetail(item.sourceable.id))
    } catch {
      showToast('We couldn’t load this part to create a revision. Try again.', 'error')
    }
  }
  // UID-300 — the RFQs/Orders tabs' own `GET /rfq?workspaceId=`/
  // `GET /orders?workspaceId=` fetches. Called unconditionally, alongside
  // `useWorkspace`/`usePartsLibrary` above, for the same Rules-of-Hooks
  // reason — this also means `orderCount` is ready for the header stat
  // card without waiting on the Orders tab to be visited.
  const { state: rfqsState } = useWorkspaceRfqs(workspaceId, rfqsService)
  const { state: ordersState } = useWorkspaceOrders(workspaceId, ordersService)
  const [ordersSortColumn, setOrdersSortColumn] = useState<OrderTableSortColumn>('placedAt')
  const [ordersSortDirection, setOrdersSortDirection] = useState<SortDirection>('descending')
  // Code-review follow-up (C1) — `RfqBundleProvider` already wraps the whole
  // app (`App.tsx`), so this is the SAME cross-screen bundle
  // `PartsLibraryPage.tsx` queues into; no new provider needed.
  const rfqBundle = useRfqBundle()
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor.
  const partsRangeSelection = useRangeSelection()
  const { selectedIds: partsSelectedIds } = partsRangeSelection
  // UID-344 (`Project Workspace.dc.html:82`): the filter row above tab
  // content. One shared box (client-side, no new fetch) rather than a
  // per-tab search state, since only one tab's content is visible at a
  // time. Corrected post-UID-603 (code-review second pass) — the BOMs/RFQs/
  // Orders lists ARE still fully loaded up front, but the Parts tab is NOT:
  // it paginates via `useLoadMoreSentinel`/`hasNextLibraryPage` below, so
  // this search only ever filters whichever pages have loaded so far for
  // that tab. See the Parts-tab render branch's own doc comment for how it
  // keeps the infinite-scroll sentinel alive through a zero-match search
  // instead of treating "no matches in what's loaded" as "no matches."
  const [tabSearch, setTabSearch] = useState('')

  function handleOrdersSort(column: OrderTableSortColumn) {
    if (column === ordersSortColumn) {
      setOrdersSortDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
    } else {
      setOrdersSortColumn(column)
      setOrdersSortDirection('descending')
    }
  }

  // Code-review follow-up — `OrderTable` is presentational (mirrors
  // `OrdersListPage.tsx`'s `filteredOrders`): it renders whatever row order
  // it's given and only reflects the sort state in `aria-sort`/the header
  // arrow. The actual reordering has to happen here, keyed on the SAME
  // state `handleOrdersSort` above updates, or clicking a column header
  // updates the arrow without ever moving a row.
  const sortedOrders = useMemo(() => {
    if (ordersState.status !== 'ready') return []
    return ordersState.orders.slice().sort((a, b) => {
      const dir = ordersSortDirection === 'ascending' ? 1 : -1
      if (ordersSortColumn === 'total') {
        return (Number(a.total.amount) - Number(b.total.amount)) * dir
      }
      const aTime = a.placedAt ? new Date(a.placedAt).getTime() : 0
      const bTime = b.placedAt ? new Date(b.placedAt).getTime() : 0
      return (aTime - bTime) * dir
    })
  }, [ordersState, ordersSortColumn, ordersSortDirection])

  const [tab, setTab] = useState<WorkspaceTab>('parts')
  const [requestBusy, setRequestBusy] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined)

  /** UID-299 — "Request quote" / above-cap volume-RFQ CTA (`PartsTable`'s
   * required `onRequestVolumeRfq` prop). Code-review follow-up (C1/C2):
   * mirrors `PartsLibraryPage.tsx`'s `handleRequestVolumeRfq` exactly — the
   * ESSENTIAL step is queuing into `RfqBundleProvider` (the SAME context
   * `RfqReviewPage.tsx` reads its `items` from) and navigating; that must
   * not be gated behind a network call. `addPartsToRfq` (`POST /parts/rfq`)
   * is a separate, best-effort server-side audit flag only — it is NOT how
   * RFQ lines actually get populated, so it fires-and-forgets AFTER the
   * essential queue+navigate step, with its own rejection swallowed via
   * `.catch()` (never an unhandled rejection). */
  function handleRequestVolumeRfq(item: LibraryPartRow) {
    // UID-335 follow-up — `thumbnailUrl` is a SIBLING field of `sourceable`
    // (never on `sourceable.imageRef` itself). See `toSafeImageRef`'s doc
    // comment for why the merge needs the root-relative-vs-absolute
    // distinction it applies.
    const imageRef = toSafeImageRef(item.thumbnailUrl, item.sourceable.id)
    // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: this CTA is a
    // "Create RFQ"/"Add to RFQ" entry point and must start a genuinely NEW
    // draft, never merge into whatever slot happened to be active.
    rfqBundle.beginDraft([
      {
        sourceable: imageRef !== undefined ? { ...item.sourceable, imageRef } : item.sourceable,
        name: item.sourceable.name ?? 'Part',
        sku: item.sourceable.catalogSku ?? null,
      },
    ])
    navigate('/rfq/new')

    void addPartsToRfq([item.sourceable.id]).catch(() => {
      // Best-effort audit flag only — see comment above. Nothing to surface.
    })
  }

  /** UID-610 — shift-click range selection over the Parts tab's currently
   * filtered/rendered row order (Gmail/Finder semantics, same as UID-470's
   * Parts Library). `ids` is the caller's `filteredParts` order at call
   * time, not captured once — see `useRangeSelection`'s doc comment. */
  function togglePartsSelect(id: string, ids: readonly string[], shiftKey: boolean) {
    partsRangeSelection.toggle(id, ids, shiftKey)
  }

  function togglePartsSelectAll(ids: readonly string[], select: boolean) {
    partsRangeSelection.toggleAll(ids, select)
  }

  if (state.status === 'loading') {
    return (
      <div aria-busy="true" aria-label="Loading project" className="workspace-skeleton">
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} height={48} />
        ))}
      </div>
    )
  }

  if (state.status === 'not_found') {
    return (
      <EmptyState
        title="Project not found"
        detail="This project may have been removed, or you may not have access to it."
        action={<Button onClick={() => navigate('/projects')}>Back to projects</Button>}
      />
    )
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title="We couldn’t load this project"
        detail={state.message}
        action={<Button onClick={refetch}>Try again</Button>}
      />
    )
  }

  const { workspace } = state

  /** Shared mutation-error affordance (review W2): every write below routes
   * failures through here instead of letting them swallow silently or
   * become an unhandled rejection. `rethrow` lets a caller (the add-member
   * modal) keep its own dialog open on failure instead of optimistically
   * closing. Typed `WorkspaceOrRedacted` (not `Workspace`) because
   * `removeMember` can now resolve to the reduced redacted stub — a
   * self-removal from a private workspace revokes the caller's own
   * visibility (security ruling, UID-48a-app 7e6ff4d). */
  async function runMutation(
    action: () => Promise<WorkspaceOrRedacted>,
    options?: { readonly rethrow?: boolean },
  ) {
    try {
      await applyMutation(action)
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Something went wrong. Try again.',
        'error',
      )
      if (options?.rethrow) throw error
    }
  }

  async function handleRequestAccess() {
    setRequestBusy(true)
    try {
      await service.requestAccess(workspace.id)
      refetch()
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Could not request access. Try again.',
        'error',
      )
    } finally {
      setRequestBusy(false)
    }
  }

  /** UID-176: "Leave project" (member self-removal via the same
   * `DELETE /members/:userId` the owner "Remove" action already uses, just
   * with the caller's own `session.userId`). Calls `removeMember` exactly
   * once, then branches on its result: a private-workspace self-leave can
   * come back as a `WorkspaceRedactedStub` (revokes the caller's own
   * visibility, same as the existing Remove path) — re-render into the
   * `NotAMemberOverlay` branch above via `applyMutation` rather than
   * `refetch`+`getWorkspace`, which would 403/404 now that access is gone.
   * An account-workspace leave comes back as a full `Workspace` (the
   * workspace itself is still visible account-wide) — just navigate away. */
  async function handleLeave() {
    if (!session) return
    try {
      const result = await service.removeMember(workspace.id, session.userId)
      showToast("You've left the project.", 'success')
      if (isRedactedWorkspace(result)) {
        await applyMutation(() => Promise.resolve(result))
      } else {
        navigate('/projects')
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Something went wrong. Try again.',
        'error',
      )
    }
  }

  /** UID-178 — owner-only "Delete project". Deliberately NOT
   * `runMutation`/`applyMutation` (those re-render this page with a
   * returned workspace); a delete navigates away instead, so it gets its
   * own try/catch mirroring `handleLeave`. The error is surfaced INSIDE the
   * still-open `ConfirmDialog` (via its `error` prop) rather than a toast,
   * so the user sees why the delete failed without the dialog closing out
   * from under them. */
  async function handleDelete() {
    // Review W1: re-entry guard against a fast double-click on the
    // ConfirmDialog's confirm button firing `deleteWorkspace` twice. The
    // dialog's own `busy` state disables that button while a confirm is in
    // flight, but that disabling only lands on the NEXT render — a second
    // click dispatched before React commits it (e.g. two synchronous click
    // events) would otherwise still reach this handler while the first
    // `deleteWorkspace` call is still in flight. Guard directly on
    // `deleting` (this component's own in-flight flag) rather than relying
    // solely on the dialog's disabled state.
    if (deleting) return
    setDeleting(true)
    setDeleteError(undefined)
    try {
      await service.deleteWorkspace(workspace.id)
      showToast('Project deleted', 'success')
      navigate('/projects')
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Something went wrong. Try again.')
    } finally {
      setDeleting(false)
    }
  }

  // `WorkspaceRedactedStub` has no `name`/`memberCount`/`description`/
  // `members`/timestamps at all (security ruling, UID-48a-app 7e6ff4d) — so
  // there is no header/stats/tabs to render for a redacted workspace, only
  // the overlay. This must key off `access === 'redacted'` and read NOTHING
  // else off `workspace`, even defensively; `isRedactedWorkspace` narrows
  // the type so the compiler enforces that below.
  if (isRedactedWorkspace(workspace)) {
    return (
      <NotAMemberOverlay
        workspace={workspace}
        onRequestAccess={handleRequestAccess}
        busy={requestBusy}
      />
    )
  }

  // UID-176: "Leave project" only renders when the caller is ACTUALLY a
  // member of THIS workspace — `session` reflects account-level auth, not
  // workspace membership, so a caller who can see this account-wide
  // workspace without being a member (`me` undefined) gets no Leave action.
  // `canLeave` still needs the actual member-row match: `workspace.myRole`
  // reflects the caller's account-level session role, not proof that this
  // session is a member of THIS workspace's member list, and we still need
  // `session.userId` (not `me`) below to pass to `removeMember`. So `me`
  // stays the membership guard/userId source here.
  // `isSoleOwner`, by contrast, switches to the idiomatic
  // `workspace.myRole === 'owner'` check already used elsewhere in this file
  // (Rename/Share/Add-member buttons) for the "am I an owner" half — `me` is
  // unnecessary there since a non-member can never reach this branch anyway
  // (gated by `canLeave` at the call site below).
  const me = session ? workspace.members.find((m) => m.userId === session.userId) : undefined
  const canLeave = !!me
  const owners = workspace.members.filter((m) => m.workspaceRole === 'owner')
  const isSoleOwner = workspace.myRole === 'owner' && owners.length === 1

  const displayName = workspace.name

  function startRename() {
    setRenameValue(displayName)
    setRenaming(true)
  }

  /** UID-177: persist the rename through `PATCH /workspaces/:id` (owner-gated)
   * via the shared `runMutation`/`applyMutation` path, so the new name survives
   * a refresh — replacing the former local-only `displayNameOverride`. A no-op
   * (blank or unchanged) just closes the editor without a server round-trip. On
   * success the editor closes; on failure `runMutation` toasts the error and we
   * keep the editor OPEN (via `rethrow`) so the user can retry or cancel. */
  async function saveRename() {
    const next = renameValue.trim()
    if (!next || next === displayName) {
      setRenaming(false)
      return
    }
    try {
      await runMutation(() => service.renameWorkspace(workspaceId, { name: next }), {
        rethrow: true,
      })
      setRenaming(false)
    } catch {
      // Error already surfaced by runMutation's toast; keep the editor open.
    }
  }

  return (
    <section aria-labelledby="workspace-heading" className="workspace-page">
      <div className="card workspace-header">
        <div className="workspace-header-top">
          {/* UID-364 (`Fictiv Platform.dc.html:2565`): the leading tinted-square
           * icon. The design tints this per-project — no such field exists on
           * the real `Workspace` contract, so every project gets the SAME
           * brand tint rather than an invented per-row color (flagged, not
           * fabricated). */}
          <span className="workspace-header-icon" aria-hidden="true">
            <FolderOutlined />
          </span>
          <div className="workspace-header-body">
            {renaming ? (
              <div className="workspace-rename-row">
                <label className="sr-only" htmlFor="workspace-rename-input">
                  Project name
                </label>
                <input
                  id="workspace-rename-input"
                  type="text"
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                />
                <Button size="sm" onClick={() => void saveRename()}>
                  Save
                </Button>
                <Button variant="link" size="sm" onClick={() => setRenaming(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="workspace-rename-row">
                <h1 id="workspace-heading" className="display-heading workspace-heading">
                  {displayName}
                </h1>
                {/* UID-364 follow-up: shown but disabled for non-owners — same
                 * pattern as "Delete project" below (`aria-disabled`, never
                 * native `disabled`, so it stays keyboard-focusable and the
                 * `title` still reaches AT). The server enforces owner-only
                 * regardless (`#requireOwner`, `updateWorkspace`); this is
                 * UX-only, matching the design's unconditional pencil icon. */}
                <button
                  type="button"
                  className="workspace-rename-icon-btn"
                  title={workspace.myRole === 'owner' ? 'Rename' : RENAME_NON_OWNER_EXPLANATION}
                  aria-label="Rename"
                  aria-disabled={workspace.myRole !== 'owner' || undefined}
                  onClick={
                    workspace.myRole === 'owner' ? startRename : (event) => event.preventDefault()
                  }
                >
                  <EditOutlined aria-hidden="true" />
                </button>
              </div>
            )}
            {workspace.description && (
              <p className="caption-text workspace-subtitle">{workspace.description}</p>
            )}
          </div>
          <div className="workspace-header-actions">
            {/* UID-364 follow-up: shown but disabled for non-owners — same
             * pattern as "Delete project" below. The server enforces
             * owner-only regardless (`#requireOwner`, `addMember`); this is
             * UX-only, matching the design's unconditional button. */}
            <Button
              variant="primary"
              size="sm"
              className="workspace-add-members-btn"
              aria-disabled={workspace.myRole !== 'owner' || undefined}
              aria-describedby={
                workspace.myRole === 'owner' ? undefined : 'workspace-add-members-non-owner-note'
              }
              title={workspace.myRole === 'owner' ? undefined : ADD_MEMBERS_NON_OWNER_EXPLANATION}
              onClick={
                workspace.myRole === 'owner'
                  ? () => setAddMemberOpen(true)
                  : (event) => event.preventDefault()
              }
            >
              <UsergroupAddOutlined aria-hidden="true" />
              Add team members
            </Button>
            {workspace.myRole !== 'owner' && (
              <p id="workspace-add-members-non-owner-note" className="sr-only">
                {ADD_MEMBERS_NON_OWNER_EXPLANATION}
              </p>
            )}
            {canLeave &&
              (isSoleOwner ? (
                <>
                  {/* Sole/last owner: kept focusable via `aria-disabled` (never
                   * native `disabled`) so the explanation reaches keyboard
                   * users too — a native `disabled` button drops out of the
                   * tab order and most browsers don't surface its `title` via
                   * accessible-name computation. UID-407 follow-up: the
                   * explanation used to render as an ALWAYS-VISIBLE caption
                   * beside the button (which the design never shows at all —
                   * the comp's own `onLeave` is unconditional, no ownership-
                   * transfer rule modeled); now it only surfaces on the actual
                   * click attempt, as a toast — an sr-only note (matching the
                   * "Add team members" non-owner pattern above) still carries
                   * it for assistive tech via `aria-describedby` without a
                   * permanently-visible line. The click handler still never
                   * calls the real `handleLeave` — the server enforces the
                   * actual last-active-owner lockout regardless, this is
                   * UX-only. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="workspace-leave-btn"
                    aria-disabled="true"
                    aria-describedby="workspace-leave-sole-owner-note"
                    title={SOLE_OWNER_LEAVE_EXPLANATION}
                    onClick={(event) => {
                      event.preventDefault()
                      showToast(SOLE_OWNER_LEAVE_EXPLANATION, 'info')
                    }}
                  >
                    Leave project
                  </Button>
                  <p id="workspace-leave-sole-owner-note" className="sr-only">
                    {SOLE_OWNER_LEAVE_EXPLANATION}
                  </p>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="workspace-leave-btn"
                  onClick={() => void handleLeave()}
                >
                  Leave project
                </Button>
              ))}
            {workspace.myRole === 'owner' ? (
              <Button
                variant="danger-outline"
                size="sm"
                className="workspace-delete-btn"
                onClick={() => setDeleteOpen(true)}
              >
                Delete project
              </Button>
            ) : (
              <>
                {/* UID-178: shown but disabled for non-owners — mirrors the
                 * sole-owner "Leave project" affordance above (`aria-disabled`,
                 * never native `disabled`, so it stays keyboard-focusable and
                 * the `title` still reaches AT). UID-407 follow-up: same
                 * toast-on-click + sr-only note treatment as "Leave project"
                 * above — the design never shows an always-visible caption
                 * here either. The click handler still never opens the real
                 * delete dialog; the server enforces owner-only regardless
                 * (`workspace:manage` + `#requireOwner`, ADR-0068 composition),
                 * this is UX-only. */}
                <Button
                  variant="danger-outline"
                  size="sm"
                  className="workspace-delete-btn"
                  aria-disabled="true"
                  aria-describedby="workspace-delete-non-owner-note"
                  title={DELETE_NON_OWNER_EXPLANATION}
                  onClick={(event) => {
                    event.preventDefault()
                    showToast(DELETE_NON_OWNER_EXPLANATION, 'info')
                  }}
                >
                  Delete project
                </Button>
                <p id="workspace-delete-non-owner-note" className="sr-only">
                  {DELETE_NON_OWNER_EXPLANATION}
                </p>
              </>
            )}
          </div>
        </div>

        {/* UID-344 (`Project Workspace.dc.html:53-64`): the design's stats
         * row is the header card's OWN bottom strip — an inline flex row on a
         * tinted background with a hairline divider, not a grid of separately
         * bordered `.card` tiles. Team members renders the same `AvatarStack`
         * (+overflow) the Members-tab toolbar already uses for this SAME
         * `workspace.members` list, instead of a bare count. */}
        <div className="workspace-stats">
          <div className="workspace-stat">
            <p className="workspace-stat-label">Team members</p>
            <AvatarStack
              people={workspace.members.map((m) => ({
                id: m.userId,
                name: m.displayName,
                avatarUrl: m.avatarUrl,
              }))}
            />
          </div>
          <div className="workspace-stat">
            <p className="workspace-stat-label">Created by</p>
            <p className="workspace-stat-value">
              {workspace.members.find((m) => m.workspaceRole === 'owner')?.displayName ?? '—'}
            </p>
          </div>
          <div className="workspace-stat">
            <p className="workspace-stat-label">Created on</p>
            <p className="workspace-stat-value">{formatDate(workspace.createdAt)}</p>
          </div>
          {/* UID-300 (`Project Workspace.dc.html:66,68` — `partCount`/
           * `orderCount`): reads the SAME `usePartsLibrary`/`useWorkspaceRfqs`/
           * `useWorkspaceOrders` state the tabs below render from, not a
           * separate count call — "—" while that fetch is still in flight
           * rather than flashing a misleading 0.
           *
           * UID-603 (ADR-0176 Amendment 1 A1.4): this view now paginates its
           * `GET /parts?workspaceId=` fetch, so `counts.parts` is no longer
           * scoped to the project on the paged path — it reverted to its
           * unfiltered-library meaning. `totalCount` is the project-scoped
           * page total and is what this stat must read. `counts.parts` stays
           * as the fallback for the (no longer reachable via this call site,
           * but still type-legal) unpaginated case. */}
          <div className="workspace-stat">
            <p className="workspace-stat-label">Parts</p>
            <p className="workspace-stat-value">
              {libraryState.status === 'ready'
                ? (libraryState.listing.totalCount ?? libraryState.listing.counts.parts)
                : '—'}
            </p>
          </div>
          <div className="workspace-stat">
            <p className="workspace-stat-label">Quotes</p>
            <p className="workspace-stat-value">
              {rfqsState.status === 'ready' ? rfqsState.rfqs.length : '—'}
            </p>
          </div>
          <div className="workspace-stat">
            <p className="workspace-stat-label">Orders</p>
            <p className="workspace-stat-value">
              {ordersState.status === 'ready' ? ordersState.orders.length : '—'}
            </p>
          </div>
        </div>
      </div>

      <Tabs
        items={[
          // UID-364 follow-up: the design's tab bar (`Fictiv Platform.dc.html`
          // `wsView.tabParts` etc.) is a bare `<span>Parts</span>` — no count
          // badge on any tab. The UID-299/300 badges below were sourced from
          // `screens.md` rather than this prototype; removed to match the
          // design this screen is actually built against.
          { id: 'parts', label: 'Parts' },
          { id: 'boms', label: 'BOMs' },
          { id: 'rfqs', label: 'RFQs' },
          { id: 'orders', label: 'Orders' },
          { id: 'members', label: 'Members' },
        ]}
        activeId={tab}
        onChange={(id) => {
          // Code-review finding W4 — `tabSearch` is one shared piece of
          // state across every tab's search/filter box; without a reset
          // here, a search entered on one tab silently filtered the next
          // tab's list too (most visible on Members, which had no
          // no-matches empty state until W3 above).
          setTab(id as WorkspaceTab)
          setTabSearch('')
        }}
        aria-label="Project sections"
        className="workspace-tabs"
      >
        {(activeId) => {
          const renderContent = (): ReactNode => {
            if (activeId === 'members') {
              // UID-412 (finding 3): the header stats strip above already renders
              // the SAME `AvatarStack` over `workspace.members` — a second copy
              // here was a duplicate the comp never shows, so this tab renders
              // straight into the row list (per-row avatars, finding 4, replace
              // its purpose).
              const filteredMembers = workspace.members.filter((member) =>
                matchesSearch(tabSearch, member.displayName, member.email),
              )
              if (filteredMembers.length === 0) {
                // Code-review finding W3 — every sibling tab has a
                // no-search-matches empty state (Parts/BOMs/RFQs/Orders);
                // Members previously had none.
                return <EmptyState title="No members match your search." />
              }
              return (
                <ul className="workspace-members-list">
                  {filteredMembers.map((member) => (
                    <li key={member.userId} className="workspace-member-row">
                      {/* Decorative — the same name is right beside it as
                       * text, so an assistive-tech user hearing the avatar's
                       * own `role="img"` label would get it announced
                       * twice (code-review finding W5). */}
                      <span aria-hidden="true">
                        <Avatar name={member.displayName} avatarUrl={member.avatarUrl} size={38} />
                      </span>
                      <span className="workspace-member-name">
                        <span className="workspace-member-name-row">
                          <span className="workspace-member-name-text">{member.displayName}</span>
                          {member.workspaceRole === 'owner' && (
                            <Chip tone="brand" className="workspace-member-owner-badge">
                              Owner
                            </Chip>
                          )}
                        </span>
                        <span className="caption-text">{member.email}</span>
                      </span>
                      <span className="workspace-member-joined">
                        Joined {formatDate(member.joinedAt)}
                      </span>
                      {workspace.myRole === 'owner' && member.workspaceRole !== 'owner' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="workspace-member-remove-btn"
                          aria-label={`Remove ${member.displayName}`}
                          onClick={() => {
                            // Deliberately not awaited (button onClick);
                            // `runMutation` catches and toasts its own
                            // errors, so nothing here becomes an
                            // unhandled rejection. The removal can now
                            // come back as a redacted stub (self-removal
                            // — handled by `applyMutation`/`useWorkspace`
                            // re-rendering this component into the
                            // redacted branch above).
                            void runMutation(() =>
                              service.removeMember(workspace.id, member.userId),
                            )
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )
            }

            // UID-299 (Slice C) / UID-300 — every tab below now renders this
            // workspace's real `?workspaceId=` slice; only Members (this
            // component's `workspace.members`) never went through a
            // placeholder.
            if (activeId === 'parts' || activeId === 'boms') {
              if (libraryState.status === 'loading') {
                return (
                  <div
                    aria-busy="true"
                    aria-label={`Loading ${activeId}`}
                    className="workspace-skeleton"
                  >
                    {SKELETON_KEYS.map((key) => (
                      <Skeleton key={key} height={48} />
                    ))}
                  </div>
                )
              }

              if (libraryState.status === 'error') {
                return (
                  <EmptyState
                    title={`We couldn’t load this project's ${activeId === 'parts' ? 'parts' : 'BOMs'}`}
                    detail={libraryState.message}
                  />
                )
              }

              const { listing } = libraryState

              if (activeId === 'boms') {
                // UID-344 (`Project Workspace.dc.html:82`): the shared toolbar
                // search filters this tab's own list client-side — the BOMs
                // list here is already fully loaded, so no new fetch.
                const filteredBoms = listing.boms.filter((bom) =>
                  matchesSearch(tabSearch, bom.name),
                )
                if (listing.boms.length > 0 && filteredBoms.length === 0) {
                  return <EmptyState title="No BOMs match your search." />
                }
                return (
                  <div className="workspace-boms-tab">
                    <WorkspaceBomsTab
                      items={filteredBoms}
                      onOpen={(id) => navigate(`/parts/boms/${id}`)}
                    />
                  </div>
                )
              }

              if (listing.parts.length === 0) {
                return (
                  <EmptyState title="No parts match — parts you add to this workspace show up here with full detail." />
                )
              }

              const filteredParts = listing.parts.filter((part) =>
                matchesSearch(tabSearch, part.sourceable.name, part.sourceable.catalogSku),
              )
              // Code-review Critical fix (UID-603 second pass, "C1
              // regressed via a different path") — `filteredParts` is a
              // client-side narrowing over whatever pages have loaded SO
              // FAR (same caveat as `PartsLibraryPage.tsx`'s W5
              // `projectFilteredParts`): `tabSearch` has no server-side `q`
              // param here, so a match on a not-yet-fetched page won't show
              // up until the user scrolls far enough to load it. Returning
              // the "No parts match" empty state whenever
              // `filteredParts.length === 0` — regardless of whether more
              // pages remained (`hasNextLibraryPage`) — used to return
              // BEFORE the sentinel below, unmounting it along with
              // everything else: typing a search term that matched zero of
              // the currently-loaded rows made the rest of the list (which
              // might contain a match, e.g. on page 2+) permanently
              // unreachable — the same user-visible defect class as the
              // original C1, just triggered by search instead of plain
              // scrolling. The "no matches" verdict now only renders once
              // there's genuinely nothing left to check
              // (`!hasNextLibraryPage` too); while more pages remain, an
              // honest in-progress state renders instead, and the sentinel
              // below stays mounted either way so scrolling keeps fetching
              // pages until a match turns up or the list is truly
              // exhausted.
              const noFilteredParts = filteredParts.length === 0

              return (
                <div className="workspace-parts-tab">
                  {noFilteredParts ? (
                    hasNextLibraryPage ? (
                      <EmptyState
                        title="Still checking this project for matches…"
                        detail="No matches in what's loaded so far — more of this project is still loading."
                      />
                    ) : (
                      <EmptyState title="No parts match your search." />
                    )
                  ) : (
                    <WorkspacePartsTab
                      items={filteredParts}
                      selectedIds={partsSelectedIds}
                      onToggleSelect={(id, shiftKey) =>
                        togglePartsSelect(
                          id,
                          filteredParts.map((p) => p.sourceable.id),
                          shiftKey,
                        )
                      }
                      onToggleSelectAll={togglePartsSelectAll}
                      onRequestVolumeRfq={handleRequestVolumeRfq}
                      onOpen={(id: string) => navigate(`/parts/${id}`)}
                      // UID-407 (finding 5) — same stub-toast posture
                      // `PartsLibraryPage` uses for these two (no backend
                      // endpoint exists for either yet); "Create revision"
                      // opens the SAME real `CreateRevisionModal`.
                      onShare={() => showToast('Sharing this part isn’t available yet.', 'info')}
                      onRemoveFromLibrary={() =>
                        showToast('Removing this part isn’t available yet.', 'info')
                      }
                      onCreateRevision={handleOpenCreateRevision}
                    />
                  )}
                  {/* Code-review C1 (UID-603) — infinite-scroll sentinel,
                    same pattern as `PartsLibraryPage.tsx`'s: ~600px lead so
                    the next page is already loading before the user
                    scrolls this tab panel to its true bottom. Only rendered
                    for the Parts tab (BOMs is never paginated, D7.1). This
                    div unmounts whenever the user switches to another tab
                    (`Tabs.tsx` renders only the active panel) and remounts
                    on return — `useLoadMoreSentinel`'s ref-callback fix
                    (code-review W1) is what keeps the observer correctly
                    attached to the CURRENT node across that cycle instead
                    of watching a stale detached one.

                    Code-review Critical fix (second pass) — gated on
                    `hasNextLibraryPage` ALONE, same as
                    `NewRfqModal.tsx`'s reference pattern, so it stays
                    mounted (and the observer keeps firing `loadMore`)
                    even while `noFilteredParts` is true above — see that
                    branch's doc comment for why. */}
                  {hasNextLibraryPage && (
                    <div
                      ref={libraryLoadMoreSentinelRef}
                      className="workspace-parts-load-more-sentinel"
                    >
                      <output aria-live="polite" className="workspace-parts-load-more-status">
                        {isLoadingMoreLibrary ? 'Loading more parts…' : ''}
                      </output>
                      {libraryLoadMoreError && (
                        <div role="alert" className="workspace-parts-load-more-error">
                          <p>{libraryLoadMoreError}</p>
                          {/* M2 (code-review UID-603 fourth pass) — a cursor
                            the server has rejected as unresolvable
                            (`library-store.ts`'s deliberate 400) can never
                            succeed via `loadMoreLibrary()`, which just
                            re-issues the SAME dead cursor forever.
                            `libraryLoadMoreErrorIsCursorRejection`
                            distinguishes that case from a generic
                            network/500 failure and retries via
                            `refetchLibrary()` (reset to page 1) instead. */}
                          <button
                            type="button"
                            className="btn-link"
                            onClick={
                              libraryLoadMoreErrorIsCursorRejection
                                ? refetchLibrary
                                : loadMoreLibrary
                            }
                          >
                            Try again
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            }

            if (activeId === 'rfqs') {
              if (rfqsState.status === 'loading') {
                return (
                  <div aria-busy="true" aria-label="Loading RFQs" className="workspace-skeleton">
                    {SKELETON_KEYS.map((key) => (
                      <Skeleton key={key} height={48} />
                    ))}
                  </div>
                )
              }
              if (rfqsState.status === 'error') {
                return (
                  <EmptyState
                    title="We couldn’t load this project's RFQs"
                    detail={rfqsState.message}
                  />
                )
              }
              // UID-344 — same client-side search posture as BOMs above,
              // matching against the SAME first-line display name
              // `WorkspaceRfqsTab` itself renders (`displayNameFor`), plus
              // the human RFQ number.
              const filteredRfqs = rfqsState.rfqs.filter((rfq) => {
                const lines = rfq.lines as readonly RfqLineView[] | undefined
                const name = lines?.[0] ? displayNameFor(lines[0].sourceable) : undefined
                return matchesSearch(tabSearch, name, rfqNumberDisplay(rfq))
              })
              if (rfqsState.rfqs.length > 0 && filteredRfqs.length === 0) {
                return <EmptyState title="No RFQs match your search." />
              }
              return (
                <WorkspaceRfqsTab items={filteredRfqs} onOpen={(id) => navigate(`/rfq/${id}`)} />
              )
            }

            // activeId === 'orders'
            if (ordersState.status === 'loading') {
              return (
                <div aria-busy="true" aria-label="Loading orders" className="workspace-skeleton">
                  {SKELETON_KEYS.map((key) => (
                    <Skeleton key={key} height={48} />
                  ))}
                </div>
              )
            }
            if (ordersState.status === 'error') {
              return (
                <EmptyState
                  title="We couldn’t load this project's orders"
                  detail={ordersState.message}
                />
              )
            }
            if (sortedOrders.length === 0) {
              // UID-182 — comp-accurate copy: `Fictiv Platform.dc.html:2786-2788` (the
              // `data-screen-label="Project"` screen) says "...in this project yet.", not
              // "...in this workspace yet." (the older `Project Workspace.dc.html` study's wording).
              return <EmptyState title="No orders placed in this project yet." />
            }
            // UID-344 — `OrderTable` already has a dedicated "no orders match
            // your search" empty state gated on `hasAnySearchOrFilterActive`
            // (previously always `false` here); wiring it up means this tab
            // doesn't need its own bespoke search-empty branch like BOMs/RFQs
            // above.
            const filteredOrders = sortedOrders.filter((order) => {
              const identity = resolveOrderIdentity(order)
              return matchesSearch(tabSearch, identity.ref, identity.name)
            })
            return (
              <div className="workspace-orders-tab">
                <OrderTable
                  orders={filteredOrders}
                  sortColumn={ordersSortColumn}
                  sortDirection={ordersSortDirection}
                  onSort={handleOrdersSort}
                  hasAnySearchOrFilterActive={tabSearch.length > 0}
                  onClearFilters={() => setTabSearch('')}
                />
              </div>
            )
          }

          return (
            <>
              {/* UID-344 (`Project Workspace.dc.html:82-85`): the filter row
               * above tab content, shared by every tab including Members
               * (UID-412 finding 1 — the comp's `phByTab`/`onPrimary` treat
               * Members as just another tab through this same row, it never
               * had a separate toolbar of its own). UID-362 wired the RFQs
               * tab's primary button into the existing `NewRfqModal`.
               * UID-364 follow-up: the design's `primaryByTab.parts`
               * ("Upload") also has an established destination — the SAME
               * `/uploads` route `PartsLibraryPage`'s own "Upload parts /
               * BOM" button uses — so it's wired here too, rather than
               * omitted. UID-413 wired BOMs' own primary button the same
               * way — see that finding's comment below for the destination
               * (NOT `/uploads`; the comp's own `onPrimary` for this tab
               * goes to the Library screen, not Uploads). */}
              <div className="workspace-toolbar">
                <div className="workspace-toolbar-search">
                  <Icon name="search" className="workspace-toolbar-search-icon" />
                  <label className="sr-only" htmlFor="workspace-tab-search">
                    {TAB_SEARCH_PLACEHOLDER[activeId as WorkspaceTab]}
                  </label>
                  <input
                    id="workspace-tab-search"
                    type="search"
                    placeholder={TAB_SEARCH_PLACEHOLDER[activeId as WorkspaceTab]}
                    value={tabSearch}
                    onChange={(event) => setTabSearch(event.target.value)}
                  />
                </div>
                {/* code-review C2 — `canUpload` (`bom:upload`) gates ONLY the
                 * menu's "Upload files or CAD" item below, not the trigger
                 * itself: "Copy from Excel"/"Add by part number" resolve to
                 * `cart:manage`/`catalog:read`, both `BUYER_BASELINE`
                 * (`authz.ts`) — held by every customer role, not just
                 * engineer. Gating the whole button on `bom:upload` would
                 * have hidden two actions a buyer/purchaser/account_admin
                 * IS permitted to use. */}
                {activeId === 'parts' && role !== null && (
                  <WorkspaceUploadMenu workspaceId={workspaceId} canUpload={canUpload} />
                )}
                {activeId === 'rfqs' && canSubmitRfq && (
                  <Button
                    variant="secondary"
                    className="rfq-new-request-btn"
                    onClick={() => setNewRfqModalOpen(true)}
                  >
                    <PlusOutlined aria-hidden="true" />
                    New RFQ
                  </Button>
                )}
                {/* UID-413 finding 1 — the comp's own `onPrimary` for this
                 * tab (`Fictiv Platform.dc.html:9955`) does NOT create a
                 * BOM directly; it navigates to the Library screen
                 * (`this.setState({libTab:'boms'}); this.go('library')`).
                 * Code-review follow-up: an earlier version of this button
                 * navigated to `/uploads` (copy-pasted from the Parts
                 * tab's own button above) — wrong destination, that's the
                 * comp's PARTS-tab affordance, not this one's. `/parts`
                 * (`PartsLibraryPage`) is the comp's actual target; this
                 * app has no query-param/prop to land directly on that
                 * page's BOMs sub-tab yet (it's a plain
                 * `useState<TabId>('parts')`, no deep-link support), so
                 * this lands on Library's default Parts view rather than
                 * its BOMs one — a real gap, but adding sub-tab deep-
                 * linking is out of this ticket's CSS/markup scope. Left
                 * ungated (no `requiredCapability` check): landing on
                 * Library's Parts view is harmless for any role, unlike the
                 * Parts tab's "Upload" button above, which UID-433 gated on
                 * `bom:upload` because its destination's own data call
                 * 403s for a role without that capability. */}
                {activeId === 'boms' && (
                  <Button
                    size="lg"
                    className="workspace-toolbar-new-bom-btn"
                    onClick={() => navigate('/parts')}
                  >
                    <PlusOutlined aria-hidden="true" />
                    New BOM
                  </Button>
                )}
                {activeId === 'members' && (
                  <>
                    {/* Code-review finding W7 — shown-but-disabled for a
                     * non-owner, not hidden, same posture as the header's
                     * "Add team members" this button duplicates the action
                     * of (`ADD_MEMBERS_NON_OWNER_EXPLANATION` above); the
                     * comp's own `showPrimary` gates on `joined`, not
                     * `owner`, so hiding it for a non-owner MEMBER was this
                     * button's own drift, not something the comp asked for. */}
                    <Button
                      size="lg"
                      className="workspace-add-member-btn"
                      aria-disabled={workspace.myRole !== 'owner' || undefined}
                      aria-describedby={
                        workspace.myRole === 'owner'
                          ? undefined
                          : 'workspace-add-member-tab-non-owner-note'
                      }
                      title={
                        workspace.myRole === 'owner' ? undefined : ADD_MEMBERS_NON_OWNER_EXPLANATION
                      }
                      onClick={
                        workspace.myRole === 'owner'
                          ? () => setAddMemberOpen(true)
                          : (event) => event.preventDefault()
                      }
                    >
                      Add team member
                    </Button>
                    {workspace.myRole !== 'owner' && (
                      <p id="workspace-add-member-tab-non-owner-note" className="sr-only">
                        {ADD_MEMBERS_NON_OWNER_EXPLANATION}
                      </p>
                    )}
                  </>
                )}
              </div>
              {renderContent()}
            </>
          )
        }}
      </Tabs>

      {newRfqModalOpen && <NewRfqModal open onClose={() => setNewRfqModalOpen(false)} />}

      {/* UID-407 (finding 5) — the SAME `CreateRevisionModal` `PartsLibraryPage`
       * mounts, opened per-row once `handleOpenCreateRevision` resolves the
       * full `PartDetail`. */}
      {revisionPart && (
        <CreateRevisionModal
          open
          onClose={() => setRevisionPart(null)}
          part={revisionPart}
          onCreated={() => refetchLibrary()}
        />
      )}

      <AddMemberModal
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        onAdd={(request) =>
          runMutation(() => service.addMember(workspace.id, request), { rethrow: true })
        }
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete project"
        description={`Delete "${displayName}"? This can’t be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onCancel={() => {
          setDeleteOpen(false)
          setDeleteError(undefined)
        }}
        onConfirm={handleDelete}
        error={deleteOpen ? deleteError : undefined}
      />
    </section>
  )
}
